import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { getLivisToken } from "../../../../helpers/storage";
import {
  getAllNotificationsAPI,
  getAllAlertsAPI,
  getOrganizationPreferencesAPI,
  getLivisUsersAPI,
  getAlertTriggerPreferencesAPI
} from "../../../../services/notificationService";
import { notificationKeys } from "../queryKeys/notificationKeys";

const AUTH_PATHS = [
  "/auth/login",
  "/app/logout/",
  "/app/no-access/",
  "/auth/forgot-password",
  "/app/terms-and-conditions",
  "/auth/terms-gate"
];

const isAuthPage = (pathname: string) =>
  AUTH_PATHS.some((path) => pathname.includes(path));

// ─── Header Notifications (polled every 15 seconds) ──────────────────────────
export const useHeaderNotificationsQuery = () => {
  const location = useLocation();

  return useQuery({
    queryKey: notificationKeys.header(),
    queryFn: async () => {
      const response = await getAllNotificationsAPI({ current: 1, limit_to: 5 });
      return response.data.data;
    },
    // ✅ Only fetch when token exists and not on auth pages
    enabled: !!getLivisToken() && !isAuthPage(location.pathname),
    // ✅ Replaces the setInterval — polls every 15 seconds
    refetchInterval: 15_000,
    // ✅ Pause polling when tab is hidden — replaces document.hidden check
    refetchIntervalInBackground: false,
    staleTime: 10_000,
    // ✅ Don't throw on error — just keep previous data
    retry: false,
  });
};

// ─── All Alerts (fetched once per session) ────────────────────────────────────
export const useAllAlertsQuery = () => {
  const location = useLocation();

  return useQuery({
    queryKey: notificationKeys.alerts(),
    queryFn: async () => {
      const response = await getAllAlertsAPI({ sort: -1 });
      return response.data.data.notifications;
    },
    enabled: !!getLivisToken() && !isAuthPage(location.pathname),
    // ✅ Fetch once per session — staleTime Infinity means no automatic refetch
    staleTime: Infinity,
    retry: false,
  });
};

// ─── Paginated Notifications (used in NotificationContent page) ───────────────
export const usePagedNotificationsQuery = (filters: {
  current: number;
  limit_to: number;
  is_read?: boolean;
  is_starred?: boolean;
}) => {
  return useQuery({
    queryKey: notificationKeys.page(filters),
    queryFn: async () => {
      const response = await getAllNotificationsAPI(filters);
      return {
        notifications: response.data.data.notifications,
        total: response.data.data.total,
      };
    },
    enabled: !!getLivisToken(),
    staleTime: 30_000,
    retry: false,
  });
};

// ─── Organization Preferences ────────────────────────────────────────────────
export const useOrganizationPreferencesQuery = (orgName: string) => {
  return useQuery({
    queryKey: notificationKeys.preferences(orgName),
    queryFn: async () => {
      const response = await getOrganizationPreferencesAPI(orgName);
      return response.data.data;
    },
    enabled: !!orgName && !!getLivisToken(),
    staleTime: 1 * 60 * 1000,
    retry: false,
  });
};

// ─── LIVIS Users ──────────────────────────────────────────────────────────────
export const useLivisUsersQuery = (orgName: string, enabled = false) => {
  return useQuery({
    queryKey: notificationKeys.livisUsers(orgName),
    queryFn: async () => {
      const response = await getLivisUsersAPI({ organization_name: orgName });
      return response.data.data.users;
    },
    // ✅ Only fetch when modal is open — caller controls via enabled param
    enabled: !!orgName && !!getLivisToken() && enabled,
    staleTime: 1 * 60 * 1000,
    retry: false,
  });
};

// ─── Alert Trigger Preferences ────────────────────────────────────────────────
export const useAlertTriggerPreferencesQuery = (orgName: string) => {
  return useQuery({
    queryKey: notificationKeys.alertTriggerPreferences(orgName),
    queryFn: async () => {
      const response = await getAlertTriggerPreferencesAPI(orgName);
      return response.data.data;
    },
    enabled: !!orgName && !!getLivisToken(),
    staleTime: 1 * 60 * 1000,
    retry: false,
  });
};